import { useEffect, useMemo, useState } from 'react';
import {
  Pane,
  TextInput,
  Button,
  Select,
  Heading,
  Switch,
  Card,
  Combobox,
  toaster,
  Checkbox,
  IconButton,
  CrossIcon,
  Badge,
} from 'evergreen-ui';
import './AdminButton.css';
import { useNavigate } from 'react-router-dom';
import useIsAdmin from './useIsAdmin';
import useStore from './useStore';
import { supabase } from './utils';
import useLoadAllData from './useLoadAllData';

const AdminButton = () => {
  const isAdmin = useIsAdmin();
  const [password, setPassword] = useState('');
  const [isError, setError] = useState(false);
  const matches = useStore((state) => state.matches);
  const categories = useStore((state) => state.categories);
  const players = useStore((state) => state.players);
  const playerCategories = useStore((state) => state.playerCategories);
  const tourneys = useStore((state) => state.tourneys);

  const [selectedMatch, setSelectedMatch] = useState(0);
  const [updatingCategory, setUpdatingCategory] = useState<number | null>(null);

  // Override states
  const [overrideCategoryId, setOverrideCategoryId] = useState<number | null>(
    null
  );
  const [overridePlayerId, setOverridePlayerId] = useState<number | null>(null);
  const [overrideSeed, setOverrideSeed] = useState<string>('');
  const [isOverriding, setIsOverriding] = useState(false);

  // Add player states
  const [newPlayerName, setNewPlayerName] = useState('');
  const [isAddingPlayer, setIsAddingPlayer] = useState(false);

  // Assign players to category states
  const [assignCategoryId, setAssignCategoryId] = useState<number | null>(null);
  const [assignPlayerId, setAssignPlayerId] = useState<number | null>(null);
  const [isAssigning, setIsAssigning] = useState(false);
  const [selectedBulkPlayerIds, setSelectedBulkPlayerIds] = useState<Set<number>>(new Set());
  const [isBulkAssigning, setIsBulkAssigning] = useState(false);
  const [newPlayerForAssign, setNewPlayerForAssign] = useState('');
  const [isCreatingAndAssigning, setIsCreatingAndAssigning] = useState(false);
  const [removingPlayerId, setRemovingPlayerId] = useState<number | null>(null);

  const navigate = useNavigate();
  const { loadAllData } = useLoadAllData();

  const currentTourney = useMemo(() => tourneys[0], [tourneys]);

  const tourneyCategories = useMemo(() => {
    if (!currentTourney) return [];
    return categories
      .filter((cat) => Number(cat.tourneyId) === currentTourney.id)
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [categories, currentTourney]);

  // Get players checked into the selected override category
  const checkedInPlayers = useMemo(() => {
    if (!overrideCategoryId) return [];
    return playerCategories
      .filter((pc) => pc.categoryId === overrideCategoryId)
      .map((pc) => {
        const player = players.find((p) => p.id === pc.playerId);
        return {
          ...pc,
          playerName: player?.name || 'Unknown',
        };
      })
      .sort((a, b) => a.playerName.localeCompare(b.playerName));
  }, [playerCategories, overrideCategoryId, players]);

  // Get players assigned to the selected assign category
  const assignedPlayersForCategory = useMemo(() => {
    if (!assignCategoryId) return [];
    return playerCategories
      .filter((pc) => pc.categoryId === assignCategoryId)
      .map((pc) => {
        const player = players.find((p) => p.id === pc.playerId);
        return {
          ...pc,
          playerName: player?.name || 'Unknown',
          isCheckedIn: pc.seed !== null,
        };
      })
      .sort((a, b) => a.playerName.localeCompare(b.playerName));
  }, [playerCategories, assignCategoryId, players]);

  // Get players NOT assigned to the selected assign category (for single add dropdown)
  const unassignedPlayersForCategory = useMemo(() => {
    if (!assignCategoryId) return [];
    const assignedIds = new Set(
      playerCategories
        .filter((pc) => pc.categoryId === assignCategoryId)
        .map((pc) => pc.playerId)
    );
    return players
      .filter((p) => !assignedIds.has(p.id))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [players, playerCategories, assignCategoryId]);

  const handleSubmit = () => {
    if (password === 'mcpicklers2026') {
      localStorage.setItem('MCPicklers-Admin', 'true');
    }
    window.location.reload();
  };

  const handleReset = async () => {
    const match = matches.find((m) => {
      if (Number(m.id) === Number(selectedMatch)) {
        return true;
      } else {
        return false;
      }
    });

    const newMatch = {
      ...match,
      score1: null,
      score2: null,
      winner: null,
      locked: null,
    };
    await supabase.from('scores').delete().in('matchId', [newMatch.id]);
    await supabase
      .from('matches')
      .upsert([newMatch])
      .then(() => {
        alert('scores reset');
      });
  };

  const handleToggleCheckin = async (categoryId: number, currentState: boolean) => {
    setUpdatingCategory(categoryId);
    try {
      const { error } = await supabase
        .from('categories')
        .update({ checkinOpen: !currentState })
        .eq('id', categoryId);

      if (error) {
        toaster.danger('Failed to update check-in status');
        console.error('Toggle check-in error:', error);
      } else {
        toaster.success(
          `Check-in ${!currentState ? 'opened' : 'closed'} for category`
        );
        await loadAllData();
      }
    } catch (err) {
      toaster.danger('An error occurred');
      console.error('Toggle check-in error:', err);
    } finally {
      setUpdatingCategory(null);
    }
  };

  const handleAddPlayer = async () => {
    const trimmedName = newPlayerName.trim();
    if (!trimmedName) {
      toaster.warning('Please enter a player name');
      return;
    }

    // Check if player already exists
    const existingPlayer = players.find(
      (p) => p.name.toLowerCase() === trimmedName.toLowerCase()
    );
    if (existingPlayer) {
      toaster.warning('A player with this name already exists');
      return;
    }

    setIsAddingPlayer(true);
    try {
      const { error } = await supabase.from('players').insert({
        name: trimmedName,
      });

      if (error) {
        toaster.danger('Failed to add player');
        console.error('Add player error:', error);
      } else {
        toaster.success(`Player "${trimmedName}" added successfully`);
        setNewPlayerName('');
        await loadAllData();
      }
    } catch (err) {
      toaster.danger('An error occurred');
      console.error('Add player error:', err);
    } finally {
      setIsAddingPlayer(false);
    }
  };

  // Assign a single player to the category (with seed = null)
  const handleAssignPlayer = async () => {
    if (!assignCategoryId || !assignPlayerId) {
      toaster.warning('Please select a category and player');
      return;
    }

    setIsAssigning(true);
    try {
      const { error } = await supabase.from('player_categories').insert({
        playerId: assignPlayerId,
        categoryId: assignCategoryId,
        seed: null,
      });

      if (error) {
        if (error.code === '23505') {
          toaster.warning('Player is already assigned to this category');
        } else {
          toaster.danger('Failed to assign player');
          console.error('Assign player error:', error);
        }
      } else {
        toaster.success('Player assigned to category');
        setAssignPlayerId(null);
        await loadAllData();
      }
    } catch (err) {
      toaster.danger('An error occurred');
      console.error('Assign player error:', err);
    } finally {
      setIsAssigning(false);
    }
  };

  // Bulk assign selected players to the category
  const handleBulkAssign = async () => {
    if (!assignCategoryId || selectedBulkPlayerIds.size === 0) {
      toaster.warning('Please select a category and at least one player');
      return;
    }

    setIsBulkAssigning(true);
    try {
      const inserts = Array.from(selectedBulkPlayerIds).map((playerId) => ({
        playerId,
        categoryId: assignCategoryId,
        seed: null,
      }));

      const { error } = await supabase.from('player_categories').insert(inserts);

      if (error) {
        toaster.danger('Failed to assign players');
        console.error('Bulk assign error:', error);
      } else {
        toaster.success(`${selectedBulkPlayerIds.size} player(s) assigned to category`);
        setSelectedBulkPlayerIds(new Set());
        await loadAllData();
      }
    } catch (err) {
      toaster.danger('An error occurred');
      console.error('Bulk assign error:', err);
    } finally {
      setIsBulkAssigning(false);
    }
  };

  // Create a new player and assign them to the category
  const handleCreateAndAssign = async () => {
    if (!assignCategoryId) {
      toaster.warning('Please select a category first');
      return;
    }

    const trimmedName = newPlayerForAssign.trim();
    if (!trimmedName) {
      toaster.warning('Please enter a player name');
      return;
    }

    // Check if player already exists
    const existingPlayer = players.find(
      (p) => p.name.toLowerCase() === trimmedName.toLowerCase()
    );
    if (existingPlayer) {
      toaster.warning('A player with this name already exists. Use the single add dropdown instead.');
      return;
    }

    setIsCreatingAndAssigning(true);
    try {
      // Create the player
      const { data: newPlayer, error: createError } = await supabase
        .from('players')
        .insert({ name: trimmedName })
        .select()
        .single();

      if (createError || !newPlayer) {
        toaster.danger('Failed to create player');
        console.error('Create player error:', createError);
        return;
      }

      // Assign to category
      const { error: assignError } = await supabase.from('player_categories').insert({
        playerId: newPlayer.id,
        categoryId: assignCategoryId,
        seed: null,
      });

      if (assignError) {
        toaster.danger('Player created but failed to assign to category');
        console.error('Assign error:', assignError);
      } else {
        toaster.success(`Player "${trimmedName}" created and assigned`);
        setNewPlayerForAssign('');
        await loadAllData();
      }
    } catch (err) {
      toaster.danger('An error occurred');
      console.error('Create and assign error:', err);
    } finally {
      setIsCreatingAndAssigning(false);
    }
  };

  // Remove a player from the category
  const handleRemoveFromCategory = async (playerId: number) => {
    if (!assignCategoryId) return;

    setRemovingPlayerId(playerId);
    try {
      const { error } = await supabase
        .from('player_categories')
        .delete()
        .eq('playerId', playerId)
        .eq('categoryId', assignCategoryId);

      if (error) {
        toaster.danger('Failed to remove player');
        console.error('Remove player error:', error);
      } else {
        toaster.success('Player removed from category');
        await loadAllData();
      }
    } catch (err) {
      toaster.danger('An error occurred');
      console.error('Remove player error:', err);
    } finally {
      setRemovingPlayerId(null);
    }
  };

  // Toggle bulk selection
  const handleBulkToggle = (playerId: number) => {
    setSelectedBulkPlayerIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(playerId)) {
        newSet.delete(playerId);
      } else {
        newSet.add(playerId);
      }
      return newSet;
    });
  };

  const handleOverrideSeed = async () => {
    if (!overrideCategoryId || !overridePlayerId) {
      toaster.warning('Please select a category and player');
      return;
    }

    const seedValue = overrideSeed.trim() === '' ? null : Number(overrideSeed);

    if (overrideSeed.trim() !== '' && isNaN(seedValue as number)) {
      toaster.warning('Seed must be a number');
      return;
    }

    setIsOverriding(true);
    try {
      // Check if player is already checked in
      const existingEntry = playerCategories.find(
        (pc) =>
          pc.categoryId === overrideCategoryId &&
          pc.playerId === overridePlayerId
      );

      if (existingEntry) {
        // Update existing entry
        const { error } = await supabase
          .from('player_categories')
          .update({ seed: seedValue })
          .eq('playerId', overridePlayerId)
          .eq('categoryId', overrideCategoryId);

        if (error) {
          toaster.danger('Failed to update seed');
          console.error('Override seed error:', error);
        } else {
          toaster.success('Seed updated successfully');
          await loadAllData();
          setOverrideSeed('');
        }
      } else {
        // Insert new entry
        const { error } = await supabase.from('player_categories').insert({
          playerId: overridePlayerId,
          categoryId: overrideCategoryId,
          seed: seedValue,
        });

        if (error) {
          toaster.danger('Failed to add player');
          console.error('Override add error:', error);
        } else {
          toaster.success('Player added with seed');
          await loadAllData();
          setOverridePlayerId(null);
          setOverrideSeed('');
        }
      }
    } catch (err) {
      toaster.danger('An error occurred');
      console.error('Override error:', err);
    } finally {
      setIsOverriding(false);
    }
  };

  useEffect(() => {
    setSelectedMatch(matches[0]?.id);
  }, [matches]);

  useEffect(() => {
    loadAllData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Pane className='admin-container'>
      {!isAdmin && (
        <>
          <TextInput
            className='admin-password-input'
            name='password'
            placeholder='Entry admin password here'
            value={password}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setPassword(e.target.value);
              if (isError) {
                setError(false);
              }
            }}
            isInvalid={isError}
          />
          {isError && (
            <div style={{ color: 'red', marginTop: '5px' }}>
              Incorrect password
            </div>
          )}
          <Button className='admin-submit-button' onClick={handleSubmit}>
            Login
          </Button>
        </>
      )}
      {isAdmin && (
        <div onClick={() => navigate('/home')} className='admin-password-input'>
          <a href='/home'>Logged in, back to home</a>
        </div>
      )}
      {isAdmin && (
        <>
          {/* Add Player Section */}
          <Card className='admin-section' elevation={1}>
            <Heading size={500} marginBottom={15}>
              Add New Player
            </Heading>
            <div className='add-player-form'>
              <TextInput
                value={newPlayerName}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setNewPlayerName(e.target.value)
                }
                placeholder='Enter player name'
                width='100%'
                marginBottom={10}
              />
              <Button
                appearance='primary'
                onClick={handleAddPlayer}
                isLoading={isAddingPlayer}
                disabled={!newPlayerName.trim()}
                width='100%'
              >
                Add Player
              </Button>
            </div>
          </Card>

          {/* Assign Players to Category Section */}
          <Card className='admin-section' elevation={1}>
            <Heading size={500} marginBottom={15}>
              Assign Players to Category
            </Heading>

            {/* Category Selection */}
            <div className='assign-category-select'>
              <label>Category:</label>
              <Select
                value={assignCategoryId?.toString() || ''}
                onChange={(e: any) => {
                  setAssignCategoryId(
                    e.target.value ? Number(e.target.value) : null
                  );
                  setSelectedBulkPlayerIds(new Set());
                  setAssignPlayerId(null);
                }}
                width='100%'
              >
                <option value=''>Select a category</option>
                {tourneyCategories.map((cat) => (
                  <option key={cat.id} value={cat.id}>
                    {cat.name}
                  </option>
                ))}
              </Select>
            </div>

            {assignCategoryId && (
              <>
                {/* Add New Player and Assign */}
                <div className='assign-subsection'>
                  <div className='assign-subsection-title'>Add New Player</div>
                  <div className='assign-new-player-form'>
                    <TextInput
                      value={newPlayerForAssign}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setNewPlayerForAssign(e.target.value)
                      }
                      placeholder='Enter name...'
                      flex={1}
                    />
                    <Button
                      appearance='primary'
                      onClick={handleCreateAndAssign}
                      isLoading={isCreatingAndAssigning}
                      disabled={!newPlayerForAssign.trim()}
                    >
                      Create & Assign
                    </Button>
                  </div>
                </div>

                {/* Single Add (existing player) */}
                <div className='assign-subsection'>
                  <div className='assign-subsection-title'>Single Add (existing player)</div>
                  <div className='assign-single-form'>
                    <Combobox
                      items={unassignedPlayersForCategory}
                      itemToString={(item) => (item ? item.name : '')}
                      onChange={(selected) =>
                        setAssignPlayerId(selected?.id || null)
                      }
                      placeholder='Search player...'
                      selectedItem={
                        unassignedPlayersForCategory.find((p) => p.id === assignPlayerId) || null
                      }
                      width='100%'
                      openOnFocus
                    />
                    <Button
                      appearance='primary'
                      onClick={handleAssignPlayer}
                      isLoading={isAssigning}
                      disabled={!assignPlayerId}
                      marginTop={10}
                    >
                      Add
                    </Button>
                  </div>
                </div>

                {/* Bulk Add */}
                {unassignedPlayersForCategory.length > 0 && (
                  <div className='assign-subsection'>
                    <div className='assign-subsection-title'>Bulk Add</div>
                    <div className='assign-bulk-list'>
                      {unassignedPlayersForCategory.map((player) => (
                        <div key={player.id} className='assign-bulk-item'>
                          <Checkbox
                            checked={selectedBulkPlayerIds.has(player.id)}
                            onChange={() => handleBulkToggle(player.id)}
                            label={player.name}
                          />
                        </div>
                      ))}
                    </div>
                    <Button
                      appearance='primary'
                      onClick={handleBulkAssign}
                      isLoading={isBulkAssigning}
                      disabled={selectedBulkPlayerIds.size === 0}
                      marginTop={10}
                      width='100%'
                    >
                      Add Selected ({selectedBulkPlayerIds.size})
                    </Button>
                  </div>
                )}

                {/* Assigned Players List */}
                <div className='assign-subsection'>
                  <div className='assign-subsection-title'>
                    Assigned Players ({assignedPlayersForCategory.length} total)
                  </div>
                  {assignedPlayersForCategory.length === 0 ? (
                    <div className='assign-empty-state'>
                      No players assigned to this category yet.
                    </div>
                  ) : (
                    <div className='assign-players-list'>
                      {assignedPlayersForCategory.map((pc) => (
                        <div key={pc.id} className='assign-player-item'>
                          <span className='assign-player-name'>{pc.playerName}</span>
                          <div className='assign-player-actions'>
                            {pc.isCheckedIn ? (
                              <Badge color='green'>Checked in</Badge>
                            ) : (
                              <Badge color='neutral'>Not checked in</Badge>
                            )}
                            <IconButton
                              icon={CrossIcon}
                              appearance='minimal'
                              intent='danger'
                              size='small'
                              onClick={() => handleRemoveFromCategory(pc.playerId)}
                              isLoading={removingPlayerId === pc.playerId}
                              disabled={removingPlayerId !== null}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}
          </Card>

          {/* Check-in Management Section */}
          <Card className='admin-section' elevation={1}>
            <Heading size={500} marginBottom={15}>
              Check-In Management
            </Heading>
            <div className='checkin-toggle-list'>
              {tourneyCategories.map((category) => (
                <div key={category.id} className='checkin-toggle-item'>
                  <span className='checkin-category-name'>{category.name}</span>
                  <Switch
                    checked={category.checkinOpen || false}
                    onChange={() =>
                      handleToggleCheckin(
                        category.id,
                        category.checkinOpen || false
                      )
                    }
                    disabled={updatingCategory === category.id}
                  />
                  <span className='checkin-status'>
                    {category.checkinOpen ? 'Open' : 'Closed'}
                  </span>
                </div>
              ))}
            </div>
          </Card>

          {/* Override Player/Seed Section */}
          <Card className='admin-section' elevation={1}>
            <Heading size={500} marginBottom={15}>
              Override Player / Seed
            </Heading>
            <div className='override-form'>
              <div className='override-row'>
                <label>Category:</label>
                <Select
                  value={overrideCategoryId?.toString() || ''}
                  onChange={(e: any) =>
                    setOverrideCategoryId(
                      e.target.value ? Number(e.target.value) : null
                    )
                  }
                  width={200}
                >
                  <option value=''>Select category</option>
                  {tourneyCategories.map((cat) => (
                    <option key={cat.id} value={cat.id}>
                      {cat.name}
                    </option>
                  ))}
                </Select>
              </div>

              <div className='override-row'>
                <label>Player:</label>
                <Combobox
                  items={players}
                  itemToString={(item) => (item ? item.name : '')}
                  onChange={(selected) =>
                    setOverridePlayerId(selected?.id || null)
                  }
                  placeholder='Select player...'
                  selectedItem={
                    players.find((p) => p.id === overridePlayerId) || null
                  }
                  width={200}
                />
              </div>

              <div className='override-row'>
                <label>Seed:</label>
                <TextInput
                  value={overrideSeed}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setOverrideSeed(e.target.value)
                  }
                  placeholder='Enter seed (optional)'
                  width={200}
                />
              </div>

              <Button
                appearance='primary'
                onClick={handleOverrideSeed}
                isLoading={isOverriding}
                disabled={!overrideCategoryId || !overridePlayerId}
              >
                Add / Update
              </Button>
            </div>

            {overrideCategoryId && checkedInPlayers.length > 0 && (
              <div className='override-current-list'>
                <Heading size={400} marginTop={20} marginBottom={10}>
                  Current Check-ins
                </Heading>
                <div className='override-players-list'>
                  {checkedInPlayers.map((pc) => (
                    <div key={pc.id} className='override-player-item'>
                      <span>{pc.playerName}</span>
                      <span className='override-player-seed'>
                        Seed: {pc.seed ?? 'N/A'}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </Card>

          {/* Reset Scores Section */}
          <div className='resetScore'>
            Reset scores
            <div className='resetScore-Width'>
              <Select
                value={selectedMatch}
                onChange={(e: any) => setSelectedMatch(e.target.value)}
              >
                {matches
                  .sort((a, b) => b.id - a.id)
                  .map((match, index) => (
                    <option key={index} value={match.id}>
                      {match.id}
                    </option>
                  ))}
              </Select>
            </div>
            <Button onClick={handleReset}>Reset</Button>
          </div>
        </>
      )}
    </Pane>
  );
};

export default AdminButton;
